import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import { updateSparepartImport, setUpdateSparepartImportId, getSparepartImport } from '../../../actions/sparepartImports';
import { getWarehouseOptions, getSparepartOptions } from '../../../actions/spare_part';
import { getSupplierOptions } from '../../../actions/material';
import { getFirmOptionsForSparepartImports } from '../../../actions/user';
import { usePermission } from '../../../hooks/usePermission';
import SparepartImportItemForm from './SparepartImportItemForm';
import { Plus } from 'react-feather';

const SparepartImportUpdateForm = ({
    updateSparepartImport,
    setUpdateSparepartImportId,
    getSparepartImport,
    getWarehouseOptions,
    getSparepartOptions,
    getFirmOptionsForSparepartImports,
    getSupplierOptions,
    spare_parts,
    sparepartImportId,
    suppliers,
    warehouses,
    firms,
    user
}) => {
    const { userCan } = usePermission();
    
    // Check if user is director or superadmin
    const isDirectorOrSuperAdmin = user?.roles?.some(role => 
        role.name === 'director' || role.name === 'superadmin'
    );
    
    const [formData, setFormData] = useState({
        supplier_id: '',
        firm_id: '',
        warehouse_id: '',
        date: new Date(),
        description: '',
        items: []
    });

    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedFirm, setSelectedFirm] = useState(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        getWarehouseOptions({ type: 'part' });
        getFirmOptionsForSparepartImports();
        getSupplierOptions();
    }, [getSparepartOptions, getWarehouseOptions, getFirmOptionsForSparepartImports, getSupplierOptions]);

    // Filter spare parts when firm changes
    useEffect(() => {
        // Only load spare parts when firm is selected
        if (selectedFirm?.value) {
            getSparepartOptions({
                firm_id: selectedFirm.value
            });
        }
    }, [selectedFirm, getSparepartOptions]);

    // Reset warehouse selection if it's no longer valid
    useEffect(() => {
        if (selectedWarehouse && warehouses.length > 0) {
            // Check if the selected warehouse is still valid for the current firm
            const isValidWarehouse = warehouses.some(w => 
                w.id === selectedWarehouse.value && 
                (!selectedFirm || w.firm_id === selectedFirm.value)
            );
            
            if (!isValidWarehouse) {
                setSelectedWarehouse(null);
            }
        }
    }, [selectedFirm, warehouses, selectedWarehouse]);

    useEffect(() => {
        if (sparepartImportId) {
            const loadSparepartImport = async () => {
                try {
                    const response = await getSparepartImport(sparepartImportId);
                    const sparepartImport = response.data;
                    
                    if (sparepartImport) {
                        // First load spare parts for this firm to ensure options are available
                        if (sparepartImport.warehouse?.firm_id) {
                            getSparepartOptions({
                                firm_id: sparepartImport.warehouse.firm_id
                            });
                        }
                        
                        // Short delay to ensure spare parts are loaded before setting form data
                        setTimeout(() => {
                            setFormData({
                                supplier_id: sparepartImport.supplier_id,
                                firm_id: sparepartImport.warehouse?.firm_id,
                                warehouse_id: sparepartImport.warehouse_id,
                                date: new Date(sparepartImport.date),
                                description: sparepartImport.description || '',
                                                        items: sparepartImport.items.map(item => ({
                            id: item.id,
                            spare_part_id: Number(item.spare_part_id),
                            quantity: item.quantity, // Use raw numeric value
                            price: item.price // Use raw numeric value
                        }))
                            });
                            
                            setSelectedSupplier({
                                value: sparepartImport.supplier_id,
                                label: sparepartImport.supplier?.name
                            });
                            
                            if (sparepartImport.warehouse?.firm_id) {
                                setSelectedFirm({
                                    value: sparepartImport.warehouse.firm_id,
                                    label: sparepartImport.warehouse.firm?.name
                                });
                            }
                            
                            setSelectedWarehouse({
                                value: sparepartImport.warehouse_id,
                                label: sparepartImport.warehouse?.name
                            });
                        }, 200);
                    }
                } catch (error) {
                    console.error('Error loading sparepart import:', error);
                }
            };
            
            loadSparepartImport();
        }
    }, [sparepartImportId, getSparepartImport, getSparepartOptions]);

    const { date, description, items } = formData;

    const onChange = e => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const onItemChange = (index, field, value) => {
        const newItems = [...items];
        // Convert quantity and price to numbers with 2 decimal places
        if (field === 'quantity' || field === 'price') {
            value = value === '' ? '' : Number(parseFloat(value).toFixed(2));
        }
        newItems[index] = { ...newItems[index], [field]: value };
        setFormData({ ...formData, items: newItems });
    };

    const addItem = () => {
        setFormData({
            ...formData,
            items: [...items, { spare_part_id: '', quantity: '', price: '' }]
        });
    };

    const removeItem = (index) => {
        if (items.length > 1) {
            const newItems = items.filter((_, i) => i !== index);
            setFormData({ ...formData, items: newItems });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!selectedSupplier) newErrors.supplier_id = 'Ta\'minotchi tanlash majburiy';
        if (!date) {
            newErrors.date = 'Sana tanlash majburiy';
        } else if (!isDirectorOrSuperAdmin) {
            // Check if date is within last 2 days for non-directors
            const selectedDate = new Date(date);
            const twoDaysAgo = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000);
            const today = new Date();
            
            if (selectedDate < twoDaysAgo || selectedDate > today) {
                newErrors.date = 'Faqat oxirgi 2 kun ichidagi sanalarni tanlashingiz mumkin';
            }
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onSubmit = e => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        // Convert all quantities and prices to numbers with 2 decimal places
        const processedItems = items.map(item => ({
            ...item,
            quantity: Number(parseFloat(item.quantity || 0).toFixed(2)),
            price: Number(parseFloat(item.price || 0).toFixed(2))
        }));
        updateSparepartImport(sparepartImportId, {
            ...formData,
            items: processedItems,
            supplier_id: selectedSupplier.value,
            // Keep existing firm_id and warehouse_id
            firm_id: formData.firm_id,
            warehouse_id: formData.warehouse_id,
            date: date.toISOString().split('T')[0]
        });
        handleClose();
    };

    const handleClose = () => {
        setUpdateSparepartImportId(null);
        setFormData({
            supplier_id: '',
            firm_id: '',
            warehouse_id: '',
            date: new Date(),
            description: '',
            items: []
        });
        setSelectedSupplier(null);
        setSelectedFirm(null);
        setSelectedWarehouse(null);
    };

    // Ensure spare part IDs are consistent by converting to numbers
    const sparePartOptions = spare_parts?.map(sparePart => ({
        value: Number(sparePart.id),  // Convert to number to ensure consistent comparison
        label: sparePart.name,
        unit: sparePart.unit
    })) || [];

    const supplierOptions = suppliers?.map(supplier => ({
        value: supplier.id,
        label: supplier.name
    })) || [];

    const firmOptions = firms?.map(firm => ({
        value: firm.id,
        label: firm.name
    })) || [];

    // Don't filter warehouses on frontend, just map what we get from the API
    const warehouseOptions = warehouses.map(warehouse => ({
        value: warehouse.id,
        label: warehouse.name
    }));
    
    // Check permissions and sparepartImportId after all hooks are called
    if (!userCan('edit sparepart imports') || !sparepartImportId) {
        return null;
    }

    return (
        <Modal show={!!sparepartImportId} onHide={handleClose} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>Ehtiyot qism importini tahrirlash</Modal.Title>
            </Modal.Header>
            <Form onSubmit={onSubmit}>
                <Modal.Body>
                    <div className="row">
                        <div className="col-md-3">
                            <Form.Group className="mb-3">
                                <Form.Label>Ta'minotchi</Form.Label>
                                <Select
                                    value={selectedSupplier}
                                    onChange={setSelectedSupplier}
                                    options={supplierOptions}
                                    placeholder="Ta'minotchini tanlang..."
                                    isSearchable={true}
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-3">
                            <Form.Group className="mb-3">
                                <Form.Label>Firma</Form.Label>
                                <Select
                                    value={selectedFirm}
                                    options={firmOptions}
                                    placeholder="Firmani tanlang..."
                                    isSearchable={true}
                                    isDisabled={true}
                                    className="disabled-select"
                                />
                                <small className="text-muted">Firma o'zgartirib bo'lmaydi</small>
                            </Form.Group>
                        </div>
                        <div className="col-md-3">
                            <Form.Group className="mb-3">
                                <Form.Label>Ombor</Form.Label>
                                <Select
                                    value={selectedWarehouse}
                                    options={warehouseOptions}
                                    placeholder="Omborni tanlang..."
                                    isSearchable={true}
                                    isDisabled={true}
                                    className="disabled-select"
                                />
                                <small className="text-muted">Ombor o'zgartirib bo'lmaydi</small>
                            </Form.Group>
                        </div>
                        <div className="col-md-3">
                            <Form.Group className="mb-3">
                                <Form.Label>Sana <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="date"
                                    name="date"
                                    value={date ? date.toISOString().split('T')[0] : ''}
                                    onChange={(e) => setFormData({ ...formData, date: new Date(e.target.value) })}
                                    max={new Date().toISOString().split('T')[0]}
                                    min={isDirectorOrSuperAdmin ? undefined : new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                                />
                                {errors.date && <div className="text-danger small">{errors.date}</div>}
                                {!isDirectorOrSuperAdmin && (
                                    <small className="text-muted">
                                        Faqat oxirgi 2 kun ichidagi sanalarni tanlashingiz mumkin
                                    </small>
                                )}
                            </Form.Group>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-12">
                            <Form.Group className="mb-3">
                                <Form.Label>Transport raqami</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="description"
                                    value={description}
                                    onChange={onChange}
                                    placeholder="Transport raqamini kiriting..."
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <div className="border rounded p-3 mb-3">
                        <h5 className="mb-3">Ehtiyot qismlar</h5>
                        {items.map((item, index) => (
                            <SparepartImportItemForm
                                key={index}
                                item={item}
                                index={index}
                                sparePartOptions={sparePartOptions}
                                onChange={onItemChange}
                                onRemove={removeItem}
                            />
                        ))}
                        <div className="d-flex justify-content-center">
                            <Button 
                                variant="outline-primary" 
                                onClick={addItem}
                                className="d-flex align-items-center"
                            >
                                <Plus size={16} className="me-2" />
                                Ehtiyot qism qo'shish
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Bekor qilish
                    </Button>
                    <Button variant="primary" type="submit">
                        Saqlash
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

SparepartImportUpdateForm.propTypes = {
    updateSparepartImport: PropTypes.func.isRequired,
    setUpdateSparepartImportId: PropTypes.func.isRequired,
    getSparepartImport: PropTypes.func.isRequired,
    getWarehouseOptions: PropTypes.func.isRequired,
    getSparepartOptions: PropTypes.func.isRequired,
    getFirmOptionsForSparepartImports: PropTypes.func.isRequired,
    getSupplierOptions: PropTypes.func.isRequired,
    spare_parts: PropTypes.array,
    suppliers: PropTypes.array,
    warehouses: PropTypes.array,
    firms: PropTypes.array,
    sparepartImportId: PropTypes.number,
    user: PropTypes.object
};

const mapStateToProps = state => ({
    spare_parts: state.spare_part.spare_part_options,
    suppliers: state.material.supplier_options,
    warehouses: state.spare_part.warehouse_options,
    firms: state.user.firm_options,
    sparepartImportId: state.sparepartImport.updateItemId,
    user: state.auth.user
});

export default connect(mapStateToProps, { 
    updateSparepartImport, 
    setUpdateSparepartImportId, 
    getSparepartImport,
    getWarehouseOptions,
    getSparepartOptions,
    getFirmOptionsForSparepartImports,
    getSupplierOptions
})(SparepartImportUpdateForm); 